import { AxiosRequestConfig, AxiosResponse } from "axios";
import { trackPromise } from "react-promise-tracker";
import { HistoryResult } from "../models/default/history-result";
import { PaginatedResult } from "../models/default/paginated-result";
import Api from "./api-config";
import { authTokenStorage } from "../utils/auth-token-storage";

export interface Dictionary {
  [index: string]: string | number | any;
}

export class BaseService<T> {
  path: string;
  parameters = new URLSearchParams();

  constructor(path: string) {
    this.path = path;
  }

  getOptions(responseType?: Dictionary | any, isFormData = false): Dictionary {
    const httpOptions: Dictionary = {};
    // Also set by api-config.tsx's request interceptor on every call made
    // through `Api` -- kept here too since some callers read `getOptions()`
    // directly rather than going through Api.
    const token = authTokenStorage.get();
    httpOptions["headers"] = {};
    if (token) {
      httpOptions["headers"]["Authorization"] = `Bearer ${token}`;
    }
    if (this.parameters) {
      httpOptions["params"] = this.parameters;
      httpOptions["paramsSerializer"] = {
        serialize: (params: URLSearchParams) => params.toString().replace(/%3B/g, ';')
      };
    }
    if (responseType) {
      httpOptions["responseType"] = responseType;
    }
    if (isFormData) {
      httpOptions["headers"]["Content-Type"] = "multipart/form-data";
    }
    const language = localStorage.getItem("lang");
    if (language) {
      const languageMappings: { [key: string]: string } = {
        pt: "pt-br",
        en: "en-us",
      };
      httpOptions["headers"]["Accept-Language"] = languageMappings[language] || language;
    }
    return httpOptions;
  }

  mountFormData(payload: any, files?: File[]): FormData {
    const newObject: Dictionary = {};
    Object.assign(newObject, payload);
    const formData = new FormData();
    // add files
    if (files != null) {
      Array.from(files).forEach(file => formData.append(file.name, file));
      // add attr object
      if (payload != null) {
        Object.keys(newObject).forEach(key => {
          if (newObject[key] != null) {
            formData.append(key, newObject[key]);
          }
        });
      }
    }
    return formData;
  }

  addParameter(key: string, value: any) {
    if (this.parameters.has(key)) {
      this.parameters.set(key, value);
    } else {
      this.parameters.append(key, value);
    }
  }

  clearParameters(): void {
    this.parameters = new URLSearchParams();
  }

  async save(aObject: T | null, isFormData = false, files?: File[]): Promise<AxiosResponse<T>> {
    if (isFormData) {
      const formData = this.mountFormData(aObject, files);
      return await trackPromise(Api.post<T>(`${this.path}`, formData, this.getOptions(null, true)));
    } else {
      return await trackPromise(Api.post<T>(this.path, aObject, this.getOptions()));
    }
  }

  async importFile(payload: T, files: File[]): Promise<AxiosResponse<T>> {
    const newObject: Dictionary = {};
    Object.assign(newObject, payload);

    const formData = new FormData();
    files.forEach(file => {
      formData.append("files", file);
    });

    if (payload) {
      Object.keys(payload).forEach(key => {
        if (newObject[key] != null) {
          formData.append(key, newObject[key]);
        }
      });
    }

    return await trackPromise(Api.post(`${this.path}`, formData, this.getOptions(null, true)));
  }

  async postFromListRoute(
    payload: Dictionary,
    route: string,
    isFormData = false,
    files?: File[],
  ): Promise<AxiosResponse<T>> {
    if (isFormData) {
      const formData = this.mountFormData(files);
      return await trackPromise(
        Api.post<T>(`${this.path}${route}/`, formData, this.getOptions(null, true)),
      );
    } else {
      return await trackPromise(Api.post<T>(`${this.path}${route}/`, payload, this.getOptions()));
    }
  }

  async saveFromDetailRoute(
    payload: Dictionary,
    id: number,
    route: string,
    isFormData = false,
    files?: File[],
  ): Promise<AxiosResponse<T>> {
    if (isFormData) {
      const formData = this.mountFormData(payload, files);
      return await trackPromise(
        Api.post<T>(`${this.path}${id}/${route}/`, formData, this.getOptions(null, true)),
      );
    } else {
      return await trackPromise(
        Api.post<T>(`${this.path}${id}/${route}/`, payload, this.getOptions()),
      );
    }
  }

  async saveFromRoute(
    payload: Dictionary,
    route: string,
    isFormData = false,
    files?: File[],
  ): Promise<AxiosResponse<T>> {
    if (isFormData) {
      const formData = this.mountFormData(payload, files);
      return await trackPromise(
        Api.post<T>(`${this.path}${route}/`, formData, this.getOptions(null, true)),
      );
    } else {
      return await trackPromise(Api.post<T>(`${this.path}${route}/`, payload, this.getOptions()));
    }
  }

  async update(payload: Dictionary, id: number): Promise<AxiosResponse<T>> {
    return await trackPromise(Api.patch<T>(`${this.path}${id}/`, payload, this.getOptions()));
  }

  async patch(payload: Dictionary, id: number): Promise<AxiosResponse<T>> {
    return await trackPromise(Api.patch<T>(`${this.path}${id}/`, payload, this.getOptions()));
  }

  async patchFromDetailRoute(
    payload: Dictionary,
    id: number,
    route: string,
  ): Promise<AxiosResponse<T>> {
    return await trackPromise(
      Api.patch<T>(`${this.path}${id}/${route}/`, payload, this.getOptions()),
    );
  }

  async getAllPaginate(): Promise<AxiosResponse<PaginatedResult<T>>> {
    return await trackPromise(Api.get(this.path, this.getOptions()));
  }

  async getAllFromListRoutePaginate(route: string): Promise<AxiosResponse<PaginatedResult<T>>> {
    return await trackPromise(Api.get(`${this.path}${route}/`, this.getOptions()));
  }

  async getFromDetailRoutePaginate(
    id: string | number,
    route: string,
  ): Promise<AxiosResponse<PaginatedResult<T>>> {
    return await trackPromise(Api.get(`${this.path}${id}/${route}/`, this.getOptions()));
  }

  async getHistory(idHistory: number): Promise<AxiosResponse<HistoryResult[]>> {
    return await trackPromise(Api.get(`${this.path}${idHistory}/history/`, this.getOptions()));
  }

  async changePassword(
    payload: Dictionary,
    id: number,
    newPassword: string,
    route: string,
  ): Promise<AxiosResponse<T>> {
    this.clearParameters();
    payload.new_password = newPassword;
    return await trackPromise(Api.patch<T>(`${this.path}${route}/`, payload, this.getOptions()));
  }

  async getAll(options?: AxiosRequestConfig): Promise<AxiosResponse<T[]>> {
    const baseOptions = this.getOptions();
    return await trackPromise(
      Api.get(this.path, {
        ...baseOptions,
        ...options,
        headers: {
          ...baseOptions.headers,
          ...options?.headers,
        },
      }),
    );
  }

  async getById(id: string | number): Promise<AxiosResponse<T>> {
    return await trackPromise(
      Api.get<T>(`${this.path}${id}/`, this.getOptions()).catch(ex => ex.response),
    );
  }

  async exportAnalysis(id: string | number): Promise<AxiosResponse<T>> {
    return await trackPromise(
      Api.get<T>(`${this.path}${id}/export/`, this.getOptions("blob")).catch(ex => ex.response),
    );
  }

  async getTemplate(): Promise<AxiosResponse<T>> {
    return await trackPromise(
      Api.get<T>(`${this.path}`, this.getOptions("blob")).catch(ex => ex.response),
    );
  }

  async getAnalysisErrorLog(id: string | number): Promise<AxiosResponse<T>> {
    return await trackPromise(
      Api.get<T>(`${this.path}${id}/log`, this.getOptions("blob")).catch(ex => ex.response),
    );
  }

  async loadUrl(url: string): Promise<AxiosResponse<T>> {
    return await trackPromise(Api.get<T>(url, this.getOptions()).catch(ex => ex.response));
  }

  async loadUrlTyped<U>(url: string): Promise<AxiosResponse<U>> {
    try {
      return await trackPromise(Api.get<U>(url, this.getOptions()));
    } catch (ex: any) {
      if (ex.response) {
        return ex.response;
      }
      throw ex; // outro erro que não seja HTTP
    }
  }

  async getBlob(url: string): Promise<AxiosResponse<Blob>> {
    return await trackPromise(Api.get(url, this.getOptions("blob")).catch(ex => ex.response));
  }

  // (Opcional) Método que recebe apenas o file_path
  async getBlobFromPath(filePath: string): Promise<AxiosResponse<Blob>> {
    const url = `export/download/?file_path=${encodeURIComponent(filePath)}`;
    return await this.getBlob(url);
  }

  async getAllFromListRoute(route: string): Promise<AxiosResponse<T[]>> {
    return await trackPromise(Api.get(`${this.path}${route}/`, this.getOptions()));
  }

  async getFromListRoute(route: string): Promise<AxiosResponse<T>> {
    return await trackPromise(Api.get(`${this.path}${route}/`, this.getOptions()));
  }

  async getFromRoutePatch(route: string, payload: Dictionary): Promise<AxiosResponse<T>> {
    return await trackPromise(Api.patch(`${this.path}${route}/`, payload, this.getOptions()));
  }

  async getFromRoutePost(route: string, payload: Dictionary): Promise<AxiosResponse<T>> {
    return await trackPromise(Api.post(`${this.path}${route}/`, payload, this.getOptions()));
  }

  async delete(id: string | number): Promise<AxiosResponse<void>> {
    return await trackPromise(Api.delete<void>(`${this.path}${id}/`, this.getOptions()));
  }

  async deleteFromDetailRoute(id: string | number, route: string): Promise<AxiosResponse<void>> {
    return await trackPromise(Api.delete<void>(`${this.path}${id}/${route}/`, this.getOptions()));
  }

  async getChoices(
    field: string,
    options?: Dictionary,
    isFormData = false,
  ): Promise<AxiosResponse<T>> {
    const mergedOptions = Object.assign({}, this.getOptions(undefined, isFormData), options);
    const response = await trackPromise(Api.options<any>(this.path, mergedOptions));
    const actions = response.data?.actions?.POST;

    if (actions && actions[field] && actions[field].choices) {
      return actions[field].choices;
    } else {
      throw new Error(`Invalid response structure for field: ${field}`);
    }
  }

  async getFromDetailRoute(id: string | number, route: string): Promise<AxiosResponse<T>> {
    return await trackPromise(Api.get<T>(`${this.path}${id}/${route}/`, this.getOptions()));
  }

  async uploadFile(route: string, files: File[]): Promise<AxiosResponse<T>> {
    const formData = new FormData();
    Array.from(files).forEach(file => {
      formData.append("file_name", file.name);
      formData.append("file", file);
    });
    return await trackPromise(
      Api.post<T>(`${this.path}${route}/`, formData, this.getOptions(null, true)),
    );
  }
}
